import type { ClientFlowAction } from '~/graphql/types.client';
import { clone, groupBy, isEmpty } from 'ramda';
import { Node, XYPosition } from 'reactflow';
import { DEFAULT_WIDTH } from '../../components/nodeTypes/constants';
import { HandlerId } from '../../components/nodeTypes/components/IfElseCard/types';
import getDirectParentIdsOfNode from '../getDirectParentIdsOfNode';

const SPACE_FOR_EDGES = 100;
const NODE_WIDTH = DEFAULT_WIDTH + SPACE_FOR_EDGES;
const NODE_DISTANCE = 200;
const PORTAL_INITIAL_OFFSET_FROM_CARD = 20;
const MAX_PORTAL_HEIGHT = 60;
const PORTAL_OFFSET_INCREMENT = 10;

type PortalNodeData = {
  /** source action id */
  sourceId?: string;

  /** target action id */
  targetId?: string;

  /** Used for source portals. Is it true path, false path or base path */
  ifElseChildKey: HandlerId | null;
};

type PositionedNode = {
  id: string;
  x: number | undefined;
  y: number;
  parentIds: Array<string>;
  children: Array<Node>;
  type?: string;
} & PortalNodeData;

type PositionedNodes = {
  [id: string]: PositionedNode;
};

type LayoutNode = Array<{
  id: string;
  data: { id: string; parentId: string } | ClientFlowAction;
  position: {
    x: number;
    y: number;
  };
  type?: string;
}>;
const createGraphLayout = (
  nodes: Array<Node>,
  renderedNodes: Array<Node<any>>,
): LayoutNode => {
  const parentToChildrenMap = {};
  const newNodes = clone(nodes);

  for (const node of newNodes) {
    if (node.type === 'empty') {
      if (parentToChildrenMap[node.data.parentId] == null) {
        parentToChildrenMap[node.data.parentId] = [];
      }

      // make sure true case is always at index 0
      parentToChildrenMap[node.data.parentId].unshift(node);
      continue;
    }

    if (node.type === 'source_portal') {
      if (parentToChildrenMap[node.data.parentId] == null) {
        parentToChildrenMap[node.data.parentId] = [];
      }

      const isFalseChild = node.data.ifElseChildKey === HandlerId.falseChild;

      parentToChildrenMap[node.data.parentId][isFalseChild ? 1 : 0] = node;
    }

    if (node?.data.action && 'parentIds' in node.data.action) {
      node.data.action.parentIds.forEach(parentId => {
        if (parentToChildrenMap[parentId] == null) {
          parentToChildrenMap[parentId] = [];
        }

        const parent = nodes.find(n => n.id === parentId);
        const isTrueChild = parent?.data?.action?.trueChildId === node.id;

        // make sure true case is always at index 0
        if (isTrueChild) {
          parentToChildrenMap[parentId].unshift(node);
        } else {
          parentToChildrenMap[parentId].push(node);
        }
      });
    }
  }

  const positionedNodes: PositionedNodes = {};

  const nodesWithMetadata = nodes.map(node => ({
    node,
    ...renderedNodes.find(n => n.id === node.id),
    parentIds: getDirectParentIdsOfNode(newNodes, node),
  }));

  const nodesByDepthLevel = groupBy(
    item => `${item.parentIds.length}`,
    nodesWithMetadata,
  );

  const rowOffsets = Object.entries(nodesByDepthLevel).reduce(
    (prev, [id, entries], index) => {
      const previousOffset = (prev[`${index - 1}`] ?? 0) + NODE_DISTANCE;

      return {
        ...prev,
        [`${id}`]:
          Math.max(...(entries ?? []).map(node => node.height ?? 0)) +
          previousOffset,
      };
    },
    {} as Record<string, number>,
  );

  nodesWithMetadata.forEach(({ node, parentIds }) => {
    const y = rowOffsets[parentIds.length - 1] ?? 0;

    positionedNodes[node.id] = {
      id: node.id,
      x: undefined,
      y,
      parentIds,
      children: parentToChildrenMap[node.id],
      type: node.type,
      sourceId: node.data?.sourceId || null,
      targetId: node.data?.targetId || null,
      ifElseChildKey: node.data?.ifElseChildKey,
    };
  });

  let emptyNodeAmount = 0;

  const addPositionX = (id: string | null, parentId: string | null) => {
    if (!id) return;

    const el = positionedNodes[id];

    if (el == null || isEmpty(el)) return;

    const newX = NODE_WIDTH * emptyNodeAmount;

    // only set x if the parentId is the direct parentId
    // so that initial positions persist after connecting another parent to the node
    const directParentId = el.parentIds[0];
    if (directParentId === parentId) {
      positionedNodes[id].x = newX;
    }

    const directChildsParentId =
      el.children?.[0]?.data?.action?.parentIds?.[0] ||
      el.children?.[0]?.data?.parentId;

    if (directChildsParentId !== id) {
      emptyNodeAmount += 1;
    }

    // Do not reset the x value for all the children of a node with multiple parents
    if (parentId && directParentId && directParentId !== parentId) return;

    /*
      Center the parent element:
      1. If current element is a false child, get its sibling's x (true child's x value),
      2. Calculate middle point with these two values
      3. Pass the calculated value to all previous parents until the previous IfElse parent
     */
    const parent = positionedNodes[directParentId];
    const isElFalseChild = parent?.children[1]?.id === id;

    if (parent?.type === 'IfElse' && isElFalseChild) {
      const trueChildId = parent.children[0].id;
      const trueChildX = positionedNodes[trueChildId].x || 0;

      for (const parentId of el.parentIds) {
        const isIfElseParent = positionedNodes[parentId].type === 'IfElse';
        const idx = el.parentIds.findIndex(n => n === parentId);

        if (idx !== 0 && isIfElseParent) {
          break;
        }

        positionedNodes[parentId].x = (trueChildX + newX) / 2;
      }
    }

    /* traverse left subtree */
    addPositionX(el.children?.[0]?.id, id);

    /* traverse right subtree */
    addPositionX(el.children?.[1]?.id, id);
  };

  addPositionX(nodes.find(n => n.type === 'Start')?.id || null, null);

  const PORTAL_OFFSET_BY_TARGET_ID: Record<string, number> = {};

  // push down target node's children (give more space for target nodes)
  for (const id in positionedNodes) {
    const element = positionedNodes[id];

    if (element.type === 'target_portal') {
      const firstIfElseParentIdx = element.parentIds.findIndex(
        id => positionedNodes[id].type === 'IfElse',
      );

      // how many action cards to skip, from the first IfElse parent until the target action
      const startFromIndex = firstIfElseParentIdx;

      // Calculate the number of target portals for this node
      const targetNodeId = element.targetId;
      if (!targetNodeId) continue;

      const targetNodesAtSameTarget = newNodes.filter(
        n => n.data?.targetId === targetNodeId && n.type === 'target_portal',
      );
      const totalPortalsForThisNode = targetNodesAtSameTarget.length;

      PORTAL_OFFSET_BY_TARGET_ID[targetNodeId] =
        PORTAL_OFFSET_INCREMENT * (totalPortalsForThisNode / 2);

      const moveChildNodesDown = (id: string, currentIndex: number) => {
        const currentEl = positionedNodes[id];

        if (!id || !currentEl) return;

        if (currentIndex >= startFromIndex) {
          positionedNodes[id].y =
            (positionedNodes[id]?.y || 0) +
            PORTAL_OFFSET_BY_TARGET_ID[targetNodeId];
        }

        if (currentEl?.children?.length > 0) {
          for (const child of currentEl.children) {
            moveChildNodesDown(child.id, currentIndex + 1);
          }
        }
        return;
      };

      // move down and level the actions on both branches of an IfElse action
      moveChildNodesDown(element.parentIds[startFromIndex], 0);
    }
  }

  // align parent and its parents in the center again
  for (const id in positionedNodes) {
    const element = positionedNodes[id];

    if (element.type === 'IfElse') {
      const trueChildId = element.children?.[0]?.id;
      const falseChildId = element.children?.[1]?.id;

      const falseChildX = falseChildId
        ? positionedNodes[falseChildId]?.x || 0
        : 0;
      const trueChildX = trueChildId ? positionedNodes[trueChildId]?.x || 0 : 0;
      const centerX = (trueChildX + falseChildX) / 2;

      positionedNodes[id].x = centerX;

      for (const parentId of element.parentIds) {
        const isIfElseParent = positionedNodes[parentId].type === 'IfElse';
        if (isIfElseParent) break;

        positionedNodes[parentId].x = centerX;
      }
    }
  }

  // give positions to target portals. do this in the end because action card positions must be determined by now
  for (const id in positionedNodes) {
    const element = positionedNodes[id];

    if (element.type === 'target_portal') {
      const targetNodeId = element.targetId;
      const targetNodesAtSameTarget = newNodes.filter(
        n => n.data?.targetId === targetNodeId && n.type === 'target_portal',
      );
      const targetNodeIdx = targetNodesAtSameTarget.findIndex(
        a => a.id === element.id,
      );

      const renderedWidth = renderedNodes.find(n => n.id === id)?.width || 0;

      const primaryParentId = element.parentIds[1];
      const isDirectFalseChildOfIfElseAction =
        nodes.find(
          n =>
            n.id === primaryParentId &&
            n.data?.action?.falseChildId === element.targetId,
        ) != null;
      const isDirectTrueChildOfIfElseAction =
        nodes.find(
          n =>
            n.id === primaryParentId &&
            n.data?.action?.trueChildId === element.targetId,
        ) != null;

      const sourceX =
        (element?.sourceId ? positionedNodes[element.sourceId]?.x : 0) || 0;
      const currentX = targetNodeId ? positionedNodes[targetNodeId].x || 0 : 0;
      const currentY = targetNodeId ? positionedNodes[targetNodeId].y : 0;

      const isOnRight = isDirectFalseChildOfIfElseAction
        ? true
        : isDirectTrueChildOfIfElseAction
          ? false
          : sourceX - currentX > 0;

      const distanceForEachPortal = MAX_PORTAL_HEIGHT * (targetNodeIdx + 1);

      // moves portals
      positionedNodes[id].x =
        currentX + (isOnRight ? DEFAULT_WIDTH - renderedWidth : 0);
      positionedNodes[id].y =
        currentY - PORTAL_INITIAL_OFFSET_FROM_CARD - distanceForEachPortal;
    }
  }

  const res = newNodes.map(n => {
    const newPosition: XYPosition = {
      x: positionedNodes?.[n.id]?.x || 0,
      y: positionedNodes?.[n.id]?.y || 0,
    };

    return {
      ...n,
      position: newPosition,
    };
  });

  return res;
};

export default createGraphLayout;
